import { MouseEvent, ReactNode } from 'react';
import styles from './ToastBanner.module.css';

export interface ToastBannerProps {
  /** Leading icon, already carrying its own data-name. */
  icon: ReactNode;
  /** Label of the action button (e.g. "View", "Accept"). */
  buttonText: string;
  /**
   * Runs when the banner body or the action button is clicked. Fires exactly
   * once per click: the button's handler stops propagation, so its clicks
   * never reach the container's handler.
   */
  onAction: () => void;
  /** data-name of the banner container. */
  dataName: string;
  /** data-name of the action button. */
  buttonDataName: string;
  /** Text content between the icon and the button. */
  children: ReactNode;
}

/**
 * Single-line toast banner layout shared by the conversation toasts (TT911
 * and interagency chat): icon, text content, action button. The button is the
 * keyboard-accessible path; the container click is a pointer-only convenience
 * with the same action. The close button react-toastify renders outside this
 * component dismisses without triggering the action.
 */
function ToastBanner({
  icon,
  buttonText,
  onAction,
  dataName,
  buttonDataName,
  children,
}: ToastBannerProps) {
  const handleButtonClick = (event: MouseEvent<HTMLButtonElement>) => {
    // Without this the click bubbles to the container's handler and the
    // action fires twice.
    event.stopPropagation();
    onAction();
  };

  return (
    <div className={styles.container} data-name={dataName} onClick={onAction}>
      <span className={styles.icon}>{icon}</span>
      <div className={styles.content}>{children}</div>
      <button
        type="button"
        className={styles.actionButton}
        data-name={buttonDataName}
        onClick={handleButtonClick}
      >
        {buttonText}
      </button>
    </div>
  );
}

export default ToastBanner;
